/**
 * API client configuration.
 */

import axios, { type AxiosInstance } from 'axios';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { config } from './env';

/**
 * Create an authenticated Axios instance for API calls.
 * 
 * Automatically includes Cognito JWT token in Authorization header.
 */
export function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: config.apiEndpoint,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add request interceptor to include JWT token
  client.interceptors.request.use(
    async (axiosConfig) => {
      try {
        const session = await fetchAuthSession();
        
        // For REST API Gateway with Cognito authorizer, we need the ID TOKEN
        // ID tokens have the 'aud' claim that API Gateway validates
        // Access tokens don't have 'aud' claim, they have 'client_id' instead
        const idToken = session.tokens?.idToken;
        
        if (!idToken) {
          console.warn('⚠️ No idToken in session');
          return axiosConfig;
        }

        // Get token string - Amplify v6 JwtToken has toString() method
        let token: string;
        if (typeof idToken === 'string') {
          token = idToken;
        } else if (idToken && typeof (idToken as any).toString === 'function') {
          token = (idToken as any).toString();
        } else {
          // Fallback: try to access token property directly
          token = (idToken as any).token || (idToken as any).value || String(idToken);
        }

        if (token && token.length > 0) {
          // Verify it looks like a JWT (has 3 parts separated by dots)
          const parts = token.split('.');
          if (parts.length === 3) {
            axiosConfig.headers.Authorization = `Bearer ${token}`;
            // Decode JWT to inspect claims (for debugging)
            try {
              const header = JSON.parse(atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')));
              const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
              console.log('✅ Token attached - JWT header:', JSON.stringify(header, null, 2));
              const tokenInfo = {
                aud: payload.aud,
                expectedClientId: config.cognitoClientId,
                audMatches: payload.aud === config.cognitoClientId,
                iss: payload.iss,
                exp: payload.exp,
                expDate: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'N/A',
                token_use: payload.token_use,
              };
              console.log('📋 Token payload info:', JSON.stringify(tokenInfo, null, 2));
              if (!tokenInfo.audMatches) {
                console.error('❌ AUDIENCE MISMATCH! Token aud:', tokenInfo.aud, 'Expected:', tokenInfo.expectedClientId);
              }
              console.log('ℹ️ Using ID TOKEN for API Gateway (token_use:', payload.token_use, ')');
            } catch (e) {
              console.warn('⚠️ Token attached but decode failed:', e);
            }
          } else {
            console.error('❌ Token does not look like a valid JWT (expected 3 parts, got', parts.length, ')');
            console.error('Token preview:', token.substring(0, 100));
            console.error('Full idToken object:', idToken);
          }
        } else {
          console.warn('⚠️ Could not extract token string from idToken');
          console.warn('idToken object:', idToken);
        }
      } catch (error) {
        console.error('❌ Failed to get auth token:', error);
        // Continue without token - API will return 401
      }

      return axiosConfig;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle 401 errors
  // Note: We don't redirect here to avoid loops - let ProtectedRoute handle it
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        // Log full error details to see if it's from API Gateway or Lambda
        const errorData = error.response?.data;
        const errorMessage = typeof errorData === 'string' ? errorData : errorData?.error || errorData?.message || JSON.stringify(errorData);
        // Log error details with proper JSON stringification
        const errorDetails = {
          url: error.config?.url,
          statusCode: error.response?.status,
          statusText: error.response?.statusText,
          errorMessage: errorMessage,
          errorData: errorData,
          responseHeaders: error.response?.headers,
          hasToken: !!error.config?.headers?.Authorization,
          authHeaderPreview: error.config?.headers?.Authorization ? error.config.headers.Authorization.substring(0, 50) + '...' : 'none',
          // Check if error is from API Gateway (no body) or Lambda (has JSON body)
          isFromApiGateway: !error.response?.data || typeof error.response.data === 'string',
        };
        console.error('401 Unauthorized - Full error details:', JSON.stringify(errorDetails, null, 2));
        // Don't redirect here - let the app's auth context handle it
        // This prevents redirect loops. The ProtectedRoute will handle redirects.
      } else if (error.response?.status === 500) {
        // Log 500 errors to diagnose Lambda issues
        const errorData = error.response?.data;
        const errorMessage = typeof errorData === 'string' ? errorData : errorData?.error || errorData?.message || JSON.stringify(errorData);
        console.error('500 Internal Server Error:', JSON.stringify({
          url: error.config?.url,
          statusCode: error.response?.status,
          statusText: error.response?.statusText,
          errorMessage: errorMessage,
          errorData: errorData,
          responseHeaders: error.response?.headers,
        }, null, 2));
      }
      return Promise.reject(error);
    }
  );

  return client;
}

/**
 * Get the current authenticated user.
 */
export async function getAuthenticatedUser() {
  try {
    const user = await getCurrentUser();
    return user;
  } catch (error) {
    return null;
  }
}

